import React, {
  useEffect,
  useState,
  useRef
} from "react";
import {
  Box,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
 
import StorefrontIcon from "@mui/icons-material/Storefront";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonIcon from "@mui/icons-material/Person";
import { api } from "../api/api";
 
import {
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";
 
import Header from "./Header";
import Sidebar from "./Sidebar";
 
export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
 
  const isMobile = useMediaQuery(
    theme.breakpoints.down("md")
  );
  const [newOrderDialog, setNewOrderDialog] = useState(false);
  const [orderId, setOrderId] = useState("");
  const lastOrderId = useRef(null);

  const getNavValue = () => {
    if (location.pathname === "/dashboard") return 0;
    if (location.pathname === "/menu") return 1;
    if (location.pathname === "/orders") return 2;
    if (location.pathname === "/financial") return 3;
    if (location.pathname === "/account") return 4;
    return 0;
  };
 
  useEffect(() => {
    // --- NEW FIX: Prevent pinch-to-zoom gestures specifically on iOS Safari ---
    const preventZoom = (e) => {
      e.preventDefault();
    };

    document.addEventListener('gesturestart', preventZoom, { passive: false });
    document.addEventListener('gesturechange', preventZoom, { passive: false });
    document.addEventListener('gestureend', preventZoom, { passive: false });
    // --------------------------------------------------------------------------

    const vendor = JSON.parse(localStorage.getItem("vendor"));
    if (!vendor?.id) return;
 
    const checkOrders = async () => {
      try {
        const res = await api.get(`/vendor/${vendor.id}/orders`);
        const orders = res.data;
 
        if (!orders?.length) return;
 
        const latestOrderId = orders[0].order_id;
        if (lastOrderId.current === null) {
          lastOrderId.current = latestOrderId;
          return;
        }
 
        if (latestOrderId !== lastOrderId.current) {
          lastOrderId.current = latestOrderId;
          setOrderId(latestOrderId);
          if (navigator.vibrate) {
            navigator.vibrate([500, 300, 500, 300, 500]);
          }
          setNewOrderDialog(true);
        }
      } catch (err) {
        console.log(err);
      }
    };
 
    checkOrders();
    const interval = setInterval(checkOrders, 5000);
 
    return () => {
      clearInterval(interval);
      // Cleanup the zoom gesture listeners when component unmounts
      document.removeEventListener('gesturestart', preventZoom);
      document.removeEventListener('gesturechange', preventZoom);
      document.removeEventListener('gestureend', preventZoom);
    };
  }, []);
 
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%", // Fixed from maxWidth: "100vw" to prevent scrollbar gaps
        overflowX: "hidden", 
        display: "flex",
        bgcolor: "#FFFDF8", // Updated to the new global warm theme color
      }}
    >
      {/* DESKTOP SIDEBAR */}
      {!isMobile && <Sidebar />}
 
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%", // Ensure flex child respects viewport and fills completely
          overflowX: "hidden" 
        }}
      >
        {/* HEADER */}
        <Header />
 
        {/* MAIN CONTENT */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            pb: isMobile ? "90px" : "20px",
            overflowX: "hidden" 
          }}
        >
          <Outlet />
        </Box>
 
        {/* MOBILE BOTTOM NAVIGATION */}
        {isMobile && (
          <Paper
            elevation={0}
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
              bgcolor: "#FFFFFF",
              boxShadow: "0 -10px 40px rgba(0,0,0,0.08)", // Modern soft upward shadow
              overflow: "hidden",
              zIndex: 1000,
            }}
          >
            <BottomNavigation
              value={getNavValue()}
              showLabels
              sx={{
                height: 72,
                bgcolor: "transparent",
                "& .MuiBottomNavigationAction-root": {
                  minWidth: "auto",
                  paddingTop: 1,
                  color: "#A59D96", // Unselected warm gray
                },
                "& .MuiBottomNavigationAction-label": {
                  fontSize: "11px",
                  marginTop: "4px",
                  fontWeight: 600,
                },
                "& .Mui-selected": {
                  color: "#3A2C23 !important", // Selected dark warm brown
                },
                "& .Mui-selected .MuiBottomNavigationAction-label": {
                  fontWeight: "900",
                },
              }}
            >
              <BottomNavigationAction
                label="Dashboard"
                icon={<StorefrontIcon />}
                onClick={() => navigate("/dashboard")}
              />
              <BottomNavigationAction
                label="Menu"
                icon={<RestaurantMenuIcon />}
                onClick={() => navigate("/menu")}
              />
              <BottomNavigationAction
                label="Orders"
                icon={<ReceiptLongIcon />}
                onClick={() => navigate("/orders")}
              />
              <BottomNavigationAction
                label="Finance"
                icon={<PaymentsIcon />}
                onClick={() => navigate("/financial")}
              />
              <BottomNavigationAction
                label="Account"
                icon={<PersonIcon />}
                onClick={() => navigate("/account")}
              />
            </BottomNavigation>
          </Paper>
        )}
      </Box>
 
      {/* MODERNIZED NEW ORDER DIALOG */}
      <Dialog
        open={newOrderDialog}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 1,
            bgcolor: '#FFFFFF',
            boxShadow: "0px 16px 48px rgba(0,0,0,0.15)",
            textAlign: "center"
          }
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            pt: 4,
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={900} color="#E85D3F" gutterBottom sx={{ letterSpacing: 0.5 }}>
            🔔 NEW ORDER RECEIVED
          </Typography>
          <Typography variant="h4" fontWeight={900} color="#3A2C23" mt={2}>
            #{orderId}
          </Typography>
        </DialogContent>
 
        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
            px: 3
          }}
        >
          <Button
            variant="contained"
            disableElevation
            fullWidth
            onClick={() => {
              setNewOrderDialog(false);
            }}
            sx={{
              borderRadius: "12px",
              py: 1.2,
              fontWeight: "900",
              bgcolor: "#F8C332",
              color: "#3A2C23",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(248, 195, 50, 0.3)",
              "&:hover": { bgcolor: "#F5B71D" }
            }}
          >
            Acknowledge Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}